// Option builders ECharts: (data, theme, cfg) => echartsOption.
// Agnósticos al dominio. El EChartsWidget los invoca según block.chart y luego
// deep-mergea block.option encima (escape hatch que analítica edita con Claude).
import { formatValue } from '../format';

// Etiqueta de eje según formato. '${value}' es el placeholder propio de ECharts
// (no un template literal de JS), por eso se silencia la regla de eslint.
// Exportado para que el modo "Código" lo exponga como helper.
export function axisFormatter(format) {
  // eslint-disable-next-line no-template-curly-in-string
  if (format === 'money') return '${value}';
  if (format === 'pct') return '{value}%';
  return '{value}';
}

/** Paleta cíclica derivada del theme. */
export function generateThemeColors(theme = {}) {
  return [
    theme.primary || '#1f77b4',
    theme.accent || '#ff7f0e',
    theme.positive || '#2ca02c',
    theme.negative || '#d62728',
    '#9467bd', '#8c564b', '#e377c2', '#7f7f7f',
  ];
}

/** (a) Línea con markLine overlays (zeroLine + targetLine). data: [{date, [y]}]. */
export function buildLineChartWithMarkLine(data, theme = {}, cfg = {}) {
  const {
    x = 'date',
    y = 'priceIndex',
    format = 'pct',
    targetValue = -10,
    smoothness = 0.4,
    showArea = true,
    zeroLineColor = '#999999',
    targetLineColor = theme.negative || '#d73027',
  } = cfg;

  const rows = Array.isArray(data) ? data : [];
  const dates = rows.map((d) => d[x]);
  const values = rows.map((d) => d[y]);

  return {
    backgroundColor: theme.pageBg || 'transparent',
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'cross', label: { backgroundColor: theme.surface } },
      formatter: (params) => {
        if (!params || params.length === 0) return '';
        const p = params[0];
        const val = p.value == null ? '—' : formatValue(p.value, format);
        return `<strong>${p.name}</strong><br/>${p.seriesName}: <strong>${val}</strong>`;
      },
    },
    grid: { left: '60px', right: '40px', top: '60px', bottom: '60px', containLabel: true },
    xAxis: {
      type: 'category', data: dates,
      axisLine: { lineStyle: { color: theme.surface } },
      axisLabel: { fontSize: 12, color: theme.headerBg },
    },
    yAxis: {
      type: 'value',
      axisLabel: { formatter: axisFormatter(format), color: theme.headerBg, fontSize: 12 },
      splitLine: { lineStyle: { color: theme.pageBg } },
    },
    series: [{
      name: 'Price Index',
      type: 'line',
      data: values,
      smooth: smoothness,
      symbolSize: 4,
      lineStyle: { color: theme.primary, width: 2 },
      itemStyle: { color: theme.primary },
      ...(showArea && {
        areaStyle: {
          color: {
            type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
            colorStops: [
              { offset: 0, color: (theme.primary || '#0E5C84') + '40' },
              { offset: 1, color: (theme.primary || '#0E5C84') + '05' },
            ],
          },
        },
      }),
      markLine: {
        silent: true,
        lineStyle: { type: 'dashed', width: 1 },
        data: [
          {
            name: 'Zero Line', yAxis: 0,
            lineStyle: { color: zeroLineColor, width: 1 },
            label: { position: 'end', formatter: formatValue(0, format) },
          },
          {
            name: 'Target', yAxis: targetValue,
            lineStyle: { color: targetLineColor, width: 2 },
            label: {
              position: 'end', formatter: `Target: ${formatValue(targetValue, format)}`,
              backgroundColor: targetLineColor, color: '#fff', borderRadius: 2, padding: [4, 8],
            },
          },
        ],
      },
    }],
  };
}

/** (b) Multi-línea. data: {dates:[...], series:[{name, data:[...]}]}. */
export function buildMultiLineChart(data, theme = {}, cfg = {}) {
  const {
    format,
    smoothness = 0.4,
    showLegend = true,
    legendPosition = 'top',
    colors = generateThemeColors(theme),
    maxSeries = 12,      // top-N cadenas (las demás se omiten para que sea legible)
    showArea = false,    // sin relleno por defecto: evita la "mancha" con muchas líneas
  } = cfg;

  const { dates = [], series: allSeries = [] } = data || {};

  // El backend ya ordena las series por cobertura (más puntos primero): tomamos
  // las top-N. Subir `maxSeries` en render_config para ver más.
  const series = maxSeries > 0 ? allSeries.slice(0, maxSeries) : allSeries;
  const hidden = Math.max(0, allSeries.length - series.length);

  const seriesOptions = series.map((s, idx) => ({
    name: s.name,
    type: 'line',
    data: s.data,
    smooth: smoothness,
    showSymbol: false,               // sin puntos: mucho menos ruido con varias líneas
    lineStyle: { color: colors[idx % colors.length], width: 1.6 },
    itemStyle: { color: colors[idx % colors.length] },
    emphasis: { focus: 'series' },   // al pasar el mouse resalta una línea y atenúa el resto
    ...(showArea && { areaStyle: { color: colors[idx % colors.length] + '12' } }),
  }));

  return {
    backgroundColor: theme.pageBg || 'transparent',
    tooltip: {
      trigger: 'axis',
      order: 'valueDesc',            // en el tooltip, el valor más alto arriba
      axisPointer: { type: 'cross', label: { backgroundColor: theme.surface } },
      formatter: (params) => {
        if (!params || params.length === 0) return '';
        let html = `<strong>${params[0].name}</strong><br/>`;
        params.forEach((p) => {
          const val = p.value == null ? 'Sin datos' : formatValue(p.value, format);
          html += `<span style="color:${p.color}">● ${p.seriesName}: <strong>${val}</strong></span><br/>`;
        });
        return html;
      },
    },
    legend: showLegend
      ? {
          type: 'scroll',            // paginada: no invade el área de la gráfica
          top: legendPosition === 'bottom' ? 'auto' : 6,
          bottom: legendPosition === 'bottom' ? 6 : 'auto',
          textStyle: { color: theme.headerBg, fontSize: 11 },
        }
      : { show: false },
    grid: { left: 8, right: 18, top: showLegend ? 44 : 20, bottom: 24, containLabel: true },
    xAxis: {
      type: 'category', data: dates, boundaryGap: false,
      axisLine: { lineStyle: { color: theme.surface } },
      axisLabel: { fontSize: 11, color: theme.headerBg },
    },
    yAxis: {
      type: 'value',
      axisLabel: { formatter: axisFormatter(format), color: theme.headerBg, fontSize: 11 },
      splitLine: { lineStyle: { color: theme.pageBg } },
    },
    series: seriesOptions,
    graphic: hidden > 0
      ? [{
          type: 'text', right: 14, bottom: 4, z: 10,
          style: { text: `+${hidden} cadenas ocultas`, fill: '#94a3b8', fontSize: 11 },
        }]
      : undefined,
  };
}

/** (c) Heatmap Departamento × Retailer con visualMap divergente.
 *  data: {departments:[...], retailers:[...], cells:{dep:{retailer:value}}}. */
export function buildHeatmapMatrix(data, theme = {}, cfg = {}) {
  const {
    negativeColor = theme.negative || '#2BB6A3',
    positiveColor = theme.positive || '#E8615E',
    min = -50,
    max = 50,
    // Por encima de esta cantidad de celdas, las etiquetas se encimarían: se
    // ocultan y el valor se lee en el tooltip. `showLabel` explícito manda.
    maxLabelCells = 120,
    labelChars = 18,        // truncado de nombres largos de retailer
  } = cfg;

  const { departments = [], retailers = [], cells = {} } = data || {};

  const heatmapData = [];
  departments.forEach((dept, depIdx) => {
    retailers.forEach((ret, retIdx) => {
      const value = cells[dept]?.[ret];
      if (value == null) return; // celdas vacías sin cobertura
      heatmapData.push([retIdx, depIdx, value]); // [xIdx, yIdx, val]
    });
  });

  const totalCells = departments.length * retailers.length;
  const showLabel = cfg.showLabel != null ? cfg.showLabel : totalCells <= maxLabelCells;
  const manyCols = retailers.length > 14;
  const truncate = (s) => (s && s.length > labelChars ? s.slice(0, labelChars - 1) + '…' : s);

  return {
    backgroundColor: theme.pageBg || 'transparent',
    tooltip: {
      position: 'top',
      formatter: (params) => {
        const [retIdx, depIdx, val] = params.value;
        return `<strong>${departments[depIdx]} × ${retailers[retIdx]}</strong><br/>Index: <strong>${Number(val).toFixed(2)}%</strong>`;
      },
    },
    // Márgenes mínimos: containLabel reserva solo lo que ocupan las etiquetas de
    // eje (no hay que sumar `left` a mano). `right` deja sitio al visualMap, que
    // NO es una etiqueta de eje y por eso no lo cubre containLabel.
    grid: { left: 8, right: 66, top: 12, bottom: 8, containLabel: true },
    xAxis: {
      type: 'category', data: retailers,
      axisLine: { lineStyle: { color: theme.surface } },
      axisLabel: {
        fontSize: 10, color: theme.headerBg, rotate: manyCols ? 60 : 35,
        interval: 0, formatter: truncate,
      },
      splitArea: { show: true },
    },
    yAxis: {
      type: 'category', data: departments,
      axisLine: { lineStyle: { color: theme.surface } },
      axisLabel: { fontSize: 11, color: theme.headerBg },
      splitArea: { show: true },
    },
    visualMap: {
      type: 'continuous',
      min, max, calculable: true, orient: 'vertical', right: 8, top: 'middle', itemHeight: 180,
      textStyle: { color: theme.headerBg },
      // Divergente con MENOS blanco: cerca de 0 ya hay tinte, los extremos saturan.
      // negativo (barato vs comp) = teal/bueno ; positivo (caro) = rojo/malo.
      inRange: { color: [negativeColor, '#bfe6df', '#f1f5f5', '#f6d6d4', positiveColor] },
      dimension: 2,
    },
    series: [{
      name: 'Price Index',
      type: 'heatmap',
      data: heatmapData,
      // Bordes blancos: separan visualmente cada celda (look de tablero limpio).
      itemStyle: { borderColor: theme.surface || '#ffffff', borderWidth: 1 },
      label: showLabel
        ? { show: true, formatter: (p) => Number(p.value[2]).toFixed(1) + '%', fontSize: 9, color: theme.headerBg }
        : { show: false },
      emphasis: { itemStyle: { borderColor: theme.primary, borderWidth: 2 } },
    }],
  };
}

/** (d) Gauge / KPI compacto con sparkline opcional.
 *  data: {current, min, max, sparkline, format}. */
export function buildKPIGauge(data = {}, theme = {}, cfg = {}) {
  const { current = 0, min = 0, max = 100, sparkline = [], format = 'pct', title: dataTitle } = data;
  const {
    title = cfg.label || dataTitle || 'KPI',
    unit = format === 'pct' ? '%' : '',
    showGauge = true,
    gaugeSplitNumber = 10,
    pointerLength = '60%',
  } = cfg;

  const series = [
    showGauge && {
      type: 'gauge',
      center: ['50%', '60%'], radius: '70%',
      min, max, splitNumber: gaugeSplitNumber,
      data: [{ value: current, name: title }],
      axisLine: { lineStyle: { color: [[0.3, theme.negative], [0.7, theme.accent], [1, theme.positive]], width: 8 } },
      pointer: { itemStyle: { color: theme.primary }, length: pointerLength },
      axisTick: { distance: -8, length: 3, lineStyle: { color: theme.surface, width: 1 } },
      splitLine: { distance: -10, length: 8, lineStyle: { color: theme.surface, width: 1 } },
      axisLabel: { color: theme.headerBg, distance: 16, fontSize: 10 },
      detail: {
        valueAnimation: true, formatter: `{value}${unit}`, offsetCenter: [0, '30%'],
        textStyle: { color: theme.primary, fontSize: 16, fontWeight: 'bold' },
      },
      progress: { itemStyle: { color: theme.primary }, roundCap: true, width: 6 },
    },
    sparkline.length > 0 && {
      name: 'Sparkline', type: 'line', data: sparkline, smooth: 0.3, symbolSize: 0,
      lineStyle: { color: theme.accent, width: 1 }, areaStyle: { color: (theme.accent || '#F68D2E') + '20' },
    },
  ].filter(Boolean);

  return {
    backgroundColor: theme.pageBg || 'transparent',
    title: { text: title, left: 'center', top: 10, textStyle: { color: theme.headerBg, fontSize: 14, fontWeight: 'bold' } },
    tooltip: { trigger: 'item' },
    series,
  };
}
